import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, Trash2, Loader2, KeyRound, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

/**
 * Settings → Account tab. Shows a "Danger zone" card with a red
 * "Delete my account permanently" button that opens a two-step confirm dialog:
 *   1. Type the account email exactly (typo guard).
 *   2. Type the current password (re-authentication).
 * On success, signs out and bounces to /login.
 */
export default function AccountSettings() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [emailConfirm, setEmailConfirm] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const isGoogle = user?.auth_provider === "google";
  const emailMatches = emailConfirm.trim().toLowerCase() === (user?.email || "").toLowerCase();
  const canDelete = emailMatches && (isGoogle || password.length > 0);

  const onConfirmDelete = async () => {
    setBusy(true);
    try {
      await api.delete("/auth/me", {
        data: { email_confirm: emailConfirm, password: isGoogle ? null : password },
      });
      toast.success("Your account has been deleted.");
      setOpen(false);
      logout();
      nav("/login", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      {!isGoogle && <ChangePasswordCard />}
      {isGoogle && (
        <Card className="rounded-3xl p-6 border-2" data-testid="google-account-card">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-2xl bg-client/12 ring-1 ring-client/25 grid place-items-center">
              <KeyRound className="w-5 h-5 text-client" />
            </div>
            <div>
              <h2 className="text-lg font-display font-black tracking-tight">Sign-in method</h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                Your account uses <b>Google sign-in</b>, so there's no password to change here. Manage your Google
                password at <a href="https://myaccount.google.com/security" target="_blank" rel="noopener noreferrer"
                                className="font-bold text-client hover:underline">myaccount.google.com/security</a>.
              </p>
            </div>
          </div>
        </Card>
      )}

      <Card className="rounded-3xl p-6 border-2 border-rose-300/50 bg-rose-50/40 dark:bg-rose-950/10 dark:border-rose-700/30 space-y-4"
          data-testid="account-danger-card">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-2xl bg-rose-500/15 ring-1 ring-rose-500/30 grid place-items-center flex-shrink-0">
          <AlertTriangle className="w-5 h-5 text-rose-600 dark:text-rose-400" />
        </div>
        <div className="flex-1">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-rose-600 dark:text-rose-400">Danger zone</p>
          <h2 className="text-lg font-display font-black tracking-tight">Delete my account permanently</h2>
          <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
            {user?.role === "client" ? (
              <>
                This will permanently remove your account <b>{user?.email}</b> and{" "}
                <b>everything you own</b> — workers, timesheets, invoices, vitals, incidents,
                kilometres, roster, medical staff, family &amp; friends, plan manager, NDIS budget,
                settings, and all approval history. This <b>cannot be undone</b>.
              </>
            ) : (
              <>
                This will permanently remove your worker account <b>{user?.email}</b>. Your
                engagement rows on every client will be removed, and your activity history on
                their accounts will be retained but show your name as <i>(deleted)</i>. This{" "}
                <b>cannot be undone</b>.
              </>
            )}
          </p>
        </div>
      </div>
      <Button
        variant="destructive"
        onClick={() => { setEmailConfirm(""); setPassword(""); setOpen(true); }}
        data-testid="open-delete-account-btn"
        className="rounded-full font-bold gap-1.5"
      >
        <Trash2 className="w-4 h-4" />
        Delete my account…
      </Button>

      <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="confirm-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-display font-black tracking-tight flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-rose-600" />
              Are you absolutely sure?
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            <p className="text-sm">
              This is permanent. Type <b className="font-mono bg-muted px-1.5 py-0.5 rounded">{user?.email}</b> below to confirm.
            </p>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-bold tracking-wider">Email</Label>
              <Input
                value={emailConfirm}
                onChange={(e) => setEmailConfirm(e.target.value)}
                placeholder={user?.email}
                className="rounded-xl min-h-[44px]"
                data-testid="delete-email-confirm"
                autoComplete="off"
              />
            </div>

            {!isGoogle && (
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-bold tracking-wider">Password</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your current password"
                  className="rounded-xl min-h-[44px]"
                  data-testid="delete-password"
                  autoComplete="current-password"
                />
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={busy}
                className="flex-1 rounded-full font-bold border-2"
                data-testid="cancel-delete-btn"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={onConfirmDelete}
                disabled={!canDelete || busy}
                className="flex-1 rounded-full font-bold gap-1.5"
                data-testid="confirm-delete-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {busy ? "Deleting…" : "Yes, delete forever"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
    </div>
  );
}


// ===================================================================
// Change password card — visible only to password-auth users.
// ===================================================================
function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [busy, setBusy] = useState(false);

  const tooShort = next.length > 0 && next.length < 8;
  const mismatch = confirm.length > 0 && confirm !== next;
  const canSubmit = current && next && next.length >= 8 && next === confirm && next !== current && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated. Use it next time you sign in.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="rounded-3xl p-6 border-2 space-y-4" data-testid="change-password-card">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-2xl bg-client/12 ring-1 ring-client/25 grid place-items-center flex-shrink-0">
          <KeyRound className="w-5 h-5 text-client" />
        </div>
        <div>
          <h2 className="text-lg font-display font-black tracking-tight">Change password</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Pick a strong new password. We'll re-check your current one before saving the change.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-3" autoComplete="off">
        <div className="space-y-1.5">
          <Label className="text-xs uppercase font-bold tracking-wider">Current password</Label>
          <PasswordField value={current} onChange={(e) => setCurrent(e.target.value)}
                         show={showCurrent} setShow={setShowCurrent}
                         dataTestId="cp-current" autoComplete="current-password" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase font-bold tracking-wider">New password</Label>
          <PasswordField value={next} onChange={(e) => setNext(e.target.value)}
                         show={showNext} setShow={setShowNext}
                         dataTestId="cp-new" autoComplete="new-password" />
          <p className={`text-xs ${tooShort ? "text-destructive" : "text-muted-foreground"}`}>
            {tooShort ? "Must be at least 8 characters." : "At least 8 characters. Mix letters, numbers and a symbol for extra safety."}
          </p>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase font-bold tracking-wider">Confirm new password</Label>
          <PasswordField value={confirm} onChange={(e) => setConfirm(e.target.value)}
                         show={showNext} setShow={setShowNext}
                         dataTestId="cp-confirm" autoComplete="new-password" />
          {mismatch && <p className="text-xs text-destructive">Passwords don't match.</p>}
        </div>
        <Button type="submit" disabled={!canSubmit}
                className="rounded-full font-bold gap-1.5"
                style={{ background: "hsl(var(--client))" }}
                data-testid="cp-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
          {busy ? "Saving…" : "Update password"}
        </Button>
      </form>
    </Card>
  );
}


function PasswordField({ value, onChange, show, setShow, dataTestId, autoComplete }) {
  return (
    <div className="relative">
      <Input
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        className="rounded-xl min-h-[44px] pr-11"
        data-testid={dataTestId}
        autoComplete={autoComplete}
      />
      <button
        type="button"
        onClick={() => setShow(!show)}
        tabIndex={-1}
        aria-label={show ? "Hide password" : "Show password"}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        data-testid={`${dataTestId}-toggle`}
      >
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}
